import {Box, Button, Modal, Typography} from '@viptech/react-components'
import {BtnClose} from 'src/components/BtnClose'
import Loading from 'src/components/Loading'
import useListApiKeys from 'src/hooks/api/api-keys/useListApiKeys'
import useRevokeApiKey from 'src/hooks/api/api-keys/useRevokeApiKey'
import useUpdateApiKey from 'src/hooks/api/api-keys/useUpdateApiKey'
import DeleteIcon from 'src/images/DeleteIcon'
import styled from 'styled-components'

type Props = {
  isOpen: boolean
  closeModal: () => void
  userIdSelected: number
  userName: string
}

export function ManageApiKeyModal({isOpen, closeModal, userIdSelected, userName}: Props) {
  const {data, isLoading} = useListApiKeys({params: {userId: userIdSelected}})

  const {mutateAsync: updateKey, isPending: pendingEdit} = useUpdateApiKey()
  const {mutateAsync: revokeKey, isPending: pendingRevoke} = useRevokeApiKey()

  const RenderValue = ({keyToRender, value}: {keyToRender: string; value: any}) => {
    return (
      <span style={{display: 'flex', gap: '5px'}}>
        <b>{keyToRender}:</b>
        {value}
      </span>
    )
  }

  const formatDate = (dateToFormat: string) => {
    return new Date(dateToFormat).toLocaleString('pt-br', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} backgroundColor='white' width='700px'>
      <Box display='flex' direction='column' padding='20px' rowGap='35px'>
        <BtnClose onClose={closeModal} />
        <Box>
          <Box display='flex' direction='row' align='center' columnGap='5px'>
            <Typography variant='span' weight={500} size='16px' children={`Gerenciar Api-Keys`}></Typography>
            <Typography variant='span' size='14px' children={`- ${userName}`}></Typography>
          </Box>
          <Typography variant='span' size='12px' color='#8E8E8E'>
            Estas são as chaves de API criadas para {userName}. Mantenha-as seguras e não compartilhe publicamente.
          </Typography>
        </Box>

        <Box overflow='auto' maxHeight='400px' minHeight='400px' display='flex' direction='column' rowGap='20px' paddingRight='8px'>
          {isLoading ? (
            <Loading width='20px' />
          ) : !data?.length ? (
            <p style={{display: 'flex', alignItems: 'center', justifyItems: 'center', margin: 'auto'}}>
              Não encontramos nenhuma chave cadastrada para o usuário
            </p>
          ) : (
            data?.map((it) => {
              return (
                <Box
                  display='flex'
                  justifyContent='space-between'
                  padding='10px'
                  borderRadius='8px'
                  border='1px solid #D0D5DD'
                  columnGap='8px'
                  fontSize='12px'
                  key={it.id}>
                  <Box display='flex' direction='column' rowGap='8px' fontSize='12px'>
                    <RenderValue keyToRender='Nome' value={it.name} />
                    <RenderValue keyToRender='Chave Parcial' value={it.maskedKey} />
                    <RenderValue keyToRender='Status' value={it.isActive ? <Tag status='Ativo'>Ativo</Tag> : <Tag status='Dasativado'>Dasativado</Tag>} />
                    <RenderValue keyToRender='Data da criação' value={formatDate(it.createdAt)} />
                    <RenderValue keyToRender='Expira em' value={it.expiresAt ? formatDate(it.expiresAt) : 'Sem expiração'} />
                    <RenderValue keyToRender='Último acesso' value={it.lastUsedAt ? formatDate(it.lastUsedAt) : 'Nunca utilizado'} />
                  </Box>

                  <Box display='flex' direction='column' rowGap='10px' justifyContent='center'>
                    <Button
                      variant='contained'
                      height='28px'
                      fontSize='10px'
                      width='130px'
                      onClick={() => updateKey({id: it.id, body: {isActive: !it.isActive}})}
                      disabled={pendingEdit || pendingRevoke}
                      disabledTextColor='#8E8E8E'>
                      {it.isActive ? 'Desativar' : 'Ativar'} chave
                    </Button>
                    <Button
                      color='#d32525'
                      hoverColor='red'
                      variant='outlined'
                      height='28px'
                      fontSize='10px'
                      onClick={() => revokeKey(it.id)}
                      disabled={pendingEdit || pendingRevoke}
                      disabledTextColor='#8E8E8E'>
                      <DeleteIcon color={pendingEdit || pendingRevoke ? '#8E8E8E' : '#d32525'} />
                      Revogar chave
                    </Button>
                  </Box>
                </Box>
              )
            })
          )}
        </Box>

        <Box display='flex' width='100%' columnGap='10px' justifyContent='flex-end'>
          <Button
            fontSize='12px'
            height='32px'
            width='120px'
            disabledTextColor='#8E8E8E'
            variant='outlined'
            disabled={pendingEdit || pendingRevoke}
            onClick={closeModal}>
            Fechar
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

const Tag = styled.div<{status: string}>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({status}) => (status === 'Ativo' ? '#CDEADD' : '#FFF3DE')};
  color: ${({status}) => (status === 'Ativo' ? '#039855' : '#F79009')};
  width: 90px;
  font-size: 12px;
  height: 20px;
  border-radius: 20px;
`
